// T203 — CSV export. Listed "ship first" in the analytics roadmap since June
// and never built.
//
// The operator's own data has been trapped in the app: 71 applications, 934
// recommendations, and no way to open any of it in a spreadsheet, hand it to
// anyone, or keep a copy that outlives a database he owns but cannot query.
//
// Client-side on purpose. The rows are already in the page — a round trip would
// add an endpoint, an auth path and a serialiser for data the browser is
// holding, to produce a file the browser can write itself.

/**
 * RFC 4180 quoting. Every field is quoted rather than only the ones that need
 * it: a conditional quote is one regex away from a file that opens with a
 * shifted column, and the size difference does not matter at this scale.
 *
 * A leading =, +, - or @ is prefixed with an apostrophe. Excel and Sheets treat
 * such a cell as a FORMULA, so a job title like "=SUM" or a note beginning with
 * a minus becomes executable content in the reader's spreadsheet. This is the
 * one thing a CSV writer must not get wrong.
 *
 * A bare numeric literal is exempt. "-3" and "+3" are numbers to every
 * spreadsheet, not formulas; escaping them exported Score and Days silent as
 * TEXT, so the columns the operator most wants to sum or sort would not.
 */
const NUMERIC = /^[+-]?(\d+\.?\d*|\.\d+)$/;

export function csvCell(value) {
  if (value === null || value === undefined) return '""';
  let s = Array.isArray(value) ? value.join('; ') : String(value);
  if (/^[=+\-@\t\r]/.test(s) && !NUMERIC.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

/**
 * Header cells are quoted only when they must be.
 *
 * downloadCsv prepends a BOM, so the file opens `\uFEFF` + the first header. A
 * parser reading it as plain UTF-8 sees the field start with the BOM, not with
 * a quote, so `"Title"` is not a quoted field and the quotes survive into the
 * value. Excel and Sheets are unaffected either way; leaving these bare costs
 * nothing, because they are labels this file chooses, not operator data.
 */
export function csvHeader(label) {
  const s = String(label ?? '');
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * @param {Array<{key: string, label: string, get?: (row) => unknown}>} columns
 * @param {Array<object>} rows
 * @returns {string} CRLF-delimited CSV with a header row, per RFC 4180.
 */
export function toCsv(columns, rows) {
  const head = columns.map((c) => csvHeader(c.label)).join(',');
  const body = (rows ?? []).map((r) =>
    columns.map((c) => csvCell(c.get ? c.get(r) : r[c.key])).join(','),
  );
  return [head, ...body].join('\r\n');
}

/**
 * Hand the file to the browser.
 *
 * The BOM is deliberate: without it Excel on Windows reads UTF-8 as Latin-1 and
 * turns every Danish job title into mojibake — "Løsningsarkitekt" becomes
 * "LÃ¸sningsarkitekt". Most of this operator's corpus is Danish.
 */
export function downloadCsv(filename, csv) {
  if (typeof document === 'undefined') return;
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers; a tick is
  // enough and the object is small.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `hunter-applications-2026-09-05.csv` */
export function csvFilename(kind, now = new Date()) {
  return `hunter-${kind}-${now.toISOString().slice(0, 10)}.csv`;
}
